import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const ReferralProgramSection = () => {
  const navigate = useNavigate();

  const commissionLevels = [
    {
      level: 'Nivel 1',
      percentage: '10%',
      description: 'Comisión por ventas directas',
      example: 'S/ 30,000 por venta de S/ 300,000',
      color: 'primary',
      icon: 'User'
    },
    {
      level: 'Nivel 2',
      percentage: '4%',
      description: 'Comisión por referidos de primer nivel',
      example: 'S/ 12,000 por venta de S/ 300,000',
      color: 'accent',
      icon: 'Users'
    },
    {
      level: 'Nivel 3',
      percentage: '0.6%',
      description: 'Comisión por referidos de segundo nivel',
      example: 'S/ 1,800 por venta de S/ 300,000',
      color: 'secondary',
      icon: 'Network'
    },
    {
      level: 'Nivel 4',
      percentage: '0.4%',
      description: 'Comisión por referidos de tercer nivel',
      example: 'S/ 1,200 por venta de S/ 300,000',
      color: 'warning',
      icon: 'TrendingUp'
    }
  ];

  const benefits = [
    {
      icon: 'Award',
      title: 'Sistema de Puntos',
      description: 'Acumula puntos mensuales y canjéalos por premios exclusivos'
    },
    {
      icon: 'BarChart3',
      title: 'Dashboard Completo',
      description: 'Visualiza tus ganancias y red de referidos en tiempo real'
    },
    {
      icon: 'Smartphone',
      title: 'CRM Integrado',
      description: 'Gestiona tus leads y clientes con herramientas profesionales'
    },
    {
      icon: 'Gift',
      title: 'Bonos Mensuales',
      description: 'Recibe bonificaciones adicionales por cumplir metas'
    }
  ];

  const handleRegisterClick = () => {
    navigate('/user-dashboard');
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Icon name="TrendingUp" size={32} color="var(--color-primary)" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Conviértete en Asesor Inmobiliario
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Únete a nuestro programa de referidos multinivel y genera ingresos extraordinarios 
            vendiendo propiedades de Residencial 3 Volcanes
          </p>
        </div>

        {/* Commission Structure */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-16">
          {commissionLevels?.map((level, index) => (
            <div
              key={index}
              className="card hover:elevation-3 transition-smooth"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Icon name={level?.icon} size={32} color="var(--color-primary)" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  {level?.level}
                </h3>
                <div className="text-4xl font-bold text-primary mb-2">
                  {level?.percentage}
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {level?.description}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground mb-1">Ejemplo:</p>
                <p className="text-sm font-medium text-foreground">
                  {level?.example}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Visual Example */}
        <div className="card bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20 mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
              Potencial de Ganancias Mensuales
            </h3>
            <p className="text-muted-foreground">
              Ejemplo con 5 ventas directas y red activa de 3 niveles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="flex items-center justify-between mb-4">
                <Icon name="User" size={24} color="var(--color-primary)" />
                <span className="text-sm font-medium text-primary">Nivel 1</span>
              </div>
              <div className="text-3xl font-bold text-foreground mb-1">
                S/ 150,000
              </div>
              <p className="text-sm text-muted-foreground">
                5 ventas × S/ 30,000
              </p>
            </div>

            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="flex items-center justify-between mb-4">
                <Icon name="Users" size={24} color="var(--color-accent)" />
                <span className="text-sm font-medium text-accent">Nivel 2</span>
              </div>
              <div className="text-3xl font-bold text-foreground mb-1">
                S/ 48,000
              </div>
              <p className="text-sm text-muted-foreground">
                10 ventas × S/ 4,800
              </p>
            </div>

            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="flex items-center justify-between mb-4">
                <Icon name="Network" size={24} color="var(--color-secondary)" />
                <span className="text-sm font-medium text-secondary">Nivel 3</span>
              </div>
              <div className="text-3xl font-bold text-foreground mb-1">
                S/ 9,000
              </div>
              <p className="text-sm text-muted-foreground">
                15 ventas × S/ 600
              </p>
            </div>

            <div className="p-6 rounded-xl bg-primary text-primary-foreground">
              <div className="flex items-center justify-between mb-4">
                <Icon name="TrendingUp" size={24} />
                <span className="text-sm font-medium">Total</span>
              </div>
              <div className="text-3xl font-bold mb-1">
                S/ 207,000
              </div>
              <p className="text-sm opacity-90">
                Ingreso mensual potencial
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12">
          {benefits?.map((benefit, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center p-6 rounded-xl hover:bg-muted/50 transition-smooth"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Icon name={benefit?.icon} size={32} color="var(--color-primary)" />
              </div>
              <h4 className="text-lg font-heading font-semibold text-foreground mb-2">
                {benefit?.title}
              </h4>
              <p className="text-muted-foreground">
                {benefit?.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="card bg-gradient-to-r from-primary to-accent text-primary-foreground text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Comienza a Generar Ingresos Hoy
            </h3>
            <p className="text-lg mb-8 opacity-90">
              Regístrate ahora y recibe capacitación completa, materiales de marketing 
              y acceso a nuestro CRM profesional sin costo alguno
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="UserPlus"
                iconPosition="left"
                onClick={handleRegisterClick}
              >
                Registrarme como Asesor
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
              >
                Descargar Brochure
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferralProgramSection;